import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Pause, Play, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { RealtimeBar, useRealtimeActivity } from '../../queries/analytics';
import { shiftDateIso, todayLocalIso } from '../../lib/date';
import { UserActivityDetailModal } from './UserActivityDetailModal';

const TIMEFRAMES = [
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '120', label: '2 hours' },
  { value: '240', label: '4 hours' },
];

const todayIso = todayLocalIso;
const shiftDate = shiftDateIso;

interface ChartUser {
  id: string;
  name: string;
  color: string;
  type: 'authenticated' | 'anonymous';
}

// All anonymous sessions collapse into this single series (keeps the legend
// readable) — the merged bar still sums every anonymous click, and clicking it
// opens the "All Anonymous Users" detail, so no individual clicks are lost.
const ANON_ID = 'anonymous';
const ANON_COLOR = '#6B7280';

// Pivot the flat (user × bucket × event) rows into one stacked row per time
// bucket, with a column per user - the shape Recharts stacks. Authenticated
// users stay per-user (named); every anonymous session merges into one column.
export function pivot(rows: RealtimeBar[]): { buckets: Record<string, number | string>[]; users: ChartUser[] } {
  const usersById = new Map<string, ChartUser>();
  const byBucket = new Map<string, Record<string, number | string>>();

  for (const row of rows) {
    const isAnon = row.user_type === 'anonymous';
    const id = isAnon ? ANON_ID : row.user_identifier;
    if (!usersById.has(id)) {
      usersById.set(id, {
        id,
        name: isAnon ? 'Anonymous session' : row.user_display_name || `${row.user_identifier.slice(0, 8)}…`,
        color: isAnon ? ANON_COLOR : row.color,
        type: row.user_type,
      });
    }
    const time = new Date(row.minute);
    const label = time.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
    // __ts orders the buckets chronologically regardless of the query's row
    // order (the live query returns newest-first, which otherwise reverses the axis).
    const bucket = byBucket.get(label) ?? { label, __ts: time.getTime() };
    bucket[id] = (Number(bucket[id]) || 0) + row.activity_count;
    byBucket.set(label, bucket);
  }

  // Named users first, the merged Anonymous column last.
  const users = Array.from(usersById.values()).sort(
    (a, b) => (a.type === 'anonymous' ? 1 : 0) - (b.type === 'anonymous' ? 1 : 0),
  );
  // Earliest time on the left, latest on the right.
  const buckets = Array.from(byBucket.values()).sort((a, b) => (Number(a.__ts) || 0) - (Number(b.__ts) || 0));
  return { buckets, users };
}

interface TooltipEntry {
  dataKey?: string | number;
  name?: string;
  value?: number | string;
  color?: string;
}

// Recharts' default tooltip shows the raw dataKey (the user id) : count. This
// shows the time, each active user's name and activity count, and the hint.
function ActivityTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipEntry[]; label?: string }) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => Number(p.value) > 0);
  if (rows.length === 0) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
      <p className="font-medium">Time: {label}</p>
      {rows.map((p) => (
        <p key={String(p.dataKey)} style={{ color: p.color }}>
          {p.name}: {p.value} {Number(p.value) === 1 ? 'activity' : 'activities'}
        </p>
      ))}
      <p className="mt-1 text-muted-foreground">Click a user in the legend for details.</p>
    </div>
  );
}

export function RealtimeActivityChart() {
  const [timeframe, setTimeframe] = useState('240');
  const [date, setDate] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedUser, setSelectedUser] = useState<ChartUser | null>(null);

  const { data, isLoading, isFetching, refetch } = useRealtimeActivity({ timeframe, date, autoRefresh });

  const { buckets, users } = useMemo(() => pivot(data?.chartData ?? []), [data]);

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">User Activity</h2>
          <p className="text-sm text-muted-foreground">
            {data ? `Activity in the ${data.summary.timeRange} · updated ${new Date(data.summary.lastUpdate).toLocaleTimeString('en-AU', { hour12: false })}` : '…'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={date ? 'today' : timeframe}
            onChange={(e) => {
              if (e.target.value === 'today') {
                setDate(todayIso());
              } else {
                setDate(null);
                setTimeframe(e.target.value);
              }
            }}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          >
            {TIMEFRAMES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
            <option value="today">Today</option>
          </select>
          {date && (
            <button
              type="button"
              onClick={() => setDate(shiftDate(date, -1))}
              aria-label="Previous day"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <input
            type="date"
            value={date ?? ''}
            onChange={(e) => setDate(e.target.value || null)}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          />
          {date && (
            <button
              type="button"
              onClick={() => setDate(shiftDate(date, 1))}
              disabled={date >= todayIso()}
              aria-label="Next day"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
          {!date && (
            <button
              type="button"
              onClick={() => setAutoRefresh((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-sm hover:bg-accent"
            >
              {autoRefresh ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {autoRefresh ? 'Pause' : 'Resume'}
            </button>
          )}
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-sm hover:bg-accent"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <Tile label="Active Users" value={data?.summary.activeUsers} accent="text-blue-600" />
        <Tile label="Total Activities" value={data?.summary.totalActivity} accent="text-emerald-600" />
        <Tile label="Unique Users" value={users.length} accent="text-primary" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : buckets.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground">No activity in this window.</p>
      ) : (
        <div className="mt-4 h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e5e7eb)" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<ActivityTooltip />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }} />
              {users.map((u) => (
                <Bar key={u.id} dataKey={u.id} stackId="a" fill={u.color} name={u.name} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {users.length > 0 && (
        <div className="mt-3">
          <p className="mb-1 text-xs font-medium text-muted-foreground">User legend (click to view details):</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {users.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => setSelectedUser(u)}
                className="inline-flex items-center gap-1.5 text-xs hover:underline"
              >
                <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: u.color }} />
                {u.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedUser && (
        <UserActivityDetailModal
          userIdentifier={selectedUser.id}
          displayName={selectedUser.name}
          date={date}
          timeframe={timeframe}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
}

function Tile({ label, value, accent }: { label: string; value: number | undefined; accent: string }) {
  return (
    <div className="rounded-lg border border-border bg-muted/40 p-3 text-center">
      <div className={`text-xl font-bold ${accent}`}>{value?.toLocaleString() ?? '—'}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
